import { SignInButton } from '@clerk/clerk-react';
import { ArrowRight, Loader2, PackageCheck } from 'lucide-react';
import { Eyebrow } from '@/components/foundry';
import { isClerkAuthConfigured, useAppAuth } from '@/lib/appAuth';
import { useShelfClaim, type ShelfClaimState } from '../lib/useShelfClaim';

// ─────────────────────────────────────────────────────────────────────────
// AfterCheckout — where a buyer lands when they paid before they had a
// sign-in.
//
// Phase 3 of docs/plans/2026-08-25-sku-shelf-portal-plan.md. This is the only
// screen in the portal designed for someone with no account, so it renders
// OUTSIDE the shell (no rail, no top bar) and above the sign-in gate.
//
// Three moments:
//   - No sign-in yet    → one instruction: use the same email you paid with.
//   - Signed in, waiting → a brief working state while the library updates.
//   - Signed in, done    → confirmation, then into the library.
//
// A delayed purchase or failed lookup stays recoverable with Check again;
// a failed check is never treated as proof that no purchase exists.
//
// Copy rule (docs/content/shared/customer-facing-voice-rule.md): second
// person, plain words, and nothing asserted that we cannot see — including no
// promise that an email has arrived.
// ─────────────────────────────────────────────────────────────────────────

export interface AfterCheckoutProps {
  /** Send the buyer on to their products. */
  onContinue: () => void;
}

export default function AfterCheckout({ onContinue }: AfterCheckoutProps) {
  const { isLoaded, isSignedIn } = useAppAuth();
  const claim = useShelfClaim();

  if (!isLoaded) {
    return (
      <Frame>
        <WorkingState label="One moment" />
      </Frame>
    );
  }

  if (!isSignedIn) {
    return (
      <Frame>
        <SignedOutCard />
      </Frame>
    );
  }

  if (claim.status === 'added') {
    return (
      <Frame>
        <AddedCard claim={claim} onContinue={onContinue} />
      </Frame>
    );
  }

  if (['none', 'unavailable', 'verify_email'].includes(claim.status)) {
    return <Frame>
      <h1 className="text-3xl font-medium tracking-tight">
        {claim.status === 'verify_email' ? 'Verify your email to continue' : 'Let’s find your product'}
      </h1>
      <p className="mt-4 leading-relaxed text-fd-text-muted">
        {claim.status === 'verify_email'
          ? 'Verify the email on your account, using the same address you paid with. Then check again.'
          : claim.status === 'unavailable'
            ? 'We couldn’t check your products just now. Please try again.'
            : 'Your payment may still be arriving. Check again in a moment, or open your products if you have already added it. Make sure you are signed in with the email you paid with.'}
      </p>
      <button type="button" onClick={claim.retry} className="mt-6 min-h-11 rounded-lg bg-fd-accent px-5 font-medium">Check again</button>
      <button type="button" onClick={onContinue} className="ml-4 mt-6 min-h-11 underline">Open your products</button>
    </Frame>;
  }

  return (
    <Frame>
      <WorkingState label="Adding it to your products" />
    </Frame>
  );
}

// ── Frame ──────────────────────────────────────────────────────────────────

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main
      className="grid min-h-[100dvh] place-items-center bg-fd-bg px-5 py-10 font-geist text-fd-text"
      style={{
        backgroundImage:
          'linear-gradient(var(--fd-grid) 1px, transparent 1px),' +
          'linear-gradient(90deg, var(--fd-grid) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        backgroundPosition: '-1px -1px',
      }}
    >
      <section className="w-full max-w-[540px] rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 shadow-[8px_8px_0_0_var(--fd-text)] sm:p-8">
        {children}
      </section>
    </main>
  );
}

// ── No sign-in yet ─────────────────────────────────────────────────────────

function SignedOutCard() {
  return (
    <>
      <Eyebrow className="inline-flex items-center gap-1.5 text-fd-teal">
        <PackageCheck className="h-3 w-3" strokeWidth={2.2} aria-hidden="true" />
        Payment complete
      </Eyebrow>

      <h1 className="mt-3 font-geist text-[30px] font-medium leading-[1.05] tracking-[-0.03em] text-fd-text sm:text-[34px]">
        You're in.
      </h1>
      {/* Race-yellow rule: brand chrome, the one accent on this screen. */}
      <span
        aria-hidden="true"
        className="mt-4 block h-[5px] w-[132px] max-w-full rounded-full bg-fd-accent"
      />

      <p className="mt-5 max-w-[46ch] text-[15px] leading-[1.6] text-fd-text-muted [text-wrap:pretty]">
        One thing left. Set up your sign-in using the <strong className="font-semibold text-fd-text">same
        email address you paid with</strong>, and what you bought will be waiting for you inside.
      </p>

      <div className="mt-6">
        {isClerkAuthConfigured ? (
          <SignInButton
            mode="modal"
            fallbackRedirectUrl={currentHref()}
            signUpFallbackRedirectUrl={currentHref()}
          >
            <button
              type="button"
              className="inline-flex h-10 items-center gap-1.5 rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[11px] font-bold uppercase tracking-[0.05em] text-[oklch(0.16_0.005_95)] shadow-[2px_2px_0_0_var(--fd-text)] transition-transform hover:-translate-y-px active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
            >
              Set up your sign-in
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden="true" />
            </button>
          </SignInButton>
        ) : (
          <span className="inline-flex h-10 items-center rounded-[5px] border border-fd-border bg-fd-surface-1 px-4 font-geist-mono text-[11px] font-bold uppercase tracking-[0.05em] text-fd-text-subtle">
            Sign-in unavailable
          </span>
        )}
      </div>

      <p className="mt-5 border-t border-fd-border pt-4 text-[13px] leading-[1.55] text-fd-text-subtle">
        Already have a sign-in here? Use that one, as long as it's on the same email address.
      </p>
    </>
  );
}

// ── Added ──────────────────────────────────────────────────────────────────

function AddedCard({ claim, onContinue }: { claim: ShelfClaimState; onContinue: () => void }) {
  const named = claim.products.filter((name) => name.trim().length > 0);

  return (
    <>
      <Eyebrow className="inline-flex items-center gap-1.5 text-fd-teal">
        <PackageCheck className="h-3 w-3" strokeWidth={2.2} aria-hidden="true" />
        Now yours
      </Eyebrow>

      <h1 className="mt-3 font-geist text-[30px] font-medium leading-[1.05] tracking-[-0.03em] text-fd-text sm:text-[34px]">
        {named.length === 1 ? `${named[0]} is yours.` : "It's yours."}
      </h1>
      <span
        aria-hidden="true"
        className="mt-4 block h-[5px] w-[132px] max-w-full rounded-full bg-fd-accent"
      />

      <p className="mt-5 max-w-[46ch] text-[15px] leading-[1.6] text-fd-text-muted [text-wrap:pretty]">
        {named.length > 1
          ? `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]} are with your products now.`
          : "It's with your products now, alongside anything else you own."}
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex h-10 items-center gap-1.5 rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent px-4 font-geist-mono text-[11px] font-bold uppercase tracking-[0.05em] text-[oklch(0.16_0.005_95)] shadow-[2px_2px_0_0_var(--fd-text)] transition-transform hover:-translate-y-px active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
        >
          Open your products
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
    </>
  );
}

// ── Working ────────────────────────────────────────────────────────────────

function WorkingState({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3" role="status">
      <Loader2 className="h-4 w-4 animate-spin text-fd-teal" aria-hidden="true" />
      <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-fd-text-muted">
        {label}
      </span>
    </div>
  );
}

function currentHref() {
  if (typeof window === 'undefined') return '/';
  return window.location.href;
}
